import { c as _c } from "react/compiler-runtime";
import { isSolanaAddress, normalizeAddress } from "@/lib/utils/address";
import { SOLANA_MAINNET_CHAIN_ID, SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { hasManualTokens, MANUAL_CHAIN_IDS } from "@b3/chain-registry";
import { useQueries, useQuery } from "@tanstack/react-query";
export interface SimTokenMetadata {
  logo?: string;
}
export interface SimBalanceItem {
  chain: string;
  chain_id: number;
  address: string;
  amount: string;
  symbol: string;
  decimals: number;
  price_usd?: number;
  value_usd?: number;
  name?: string;
  token_metadata?: SimTokenMetadata;
  pool_size?: number;
  low_liquidity?: boolean;
}
export interface SimBalanceResponse {
  request_time: string;
  response_time: string;
  wallet_address: string;
  balances: SimBalanceItem[];
  next_offset?: string;
}
export interface SimTokenInfo {
  chain: string;
  chain_id: number;
  price_usd?: number;
  pool_size?: number;
  total_supply?: string;
  fully_diluted_value?: number;
  symbol: string;
  name?: string;
  decimals: number;
  logo?: string;
  token_metadata?: SimTokenMetadata;
}
export interface SimTokenInfoResponse {
  contract_address: string;
  tokens: SimTokenInfo[];
}
export const SIMDUNE_API_HOST = "https://simdune-api.sean-430.workers.dev";

/**
 * Builds a Simdune API URL with the proxy wrapper and optional dev mode key.
 * @param endpoint - The Simdune API endpoint (e.g., "/v1/evm/token-info/0x...")
 * @param queryParams - Optional URLSearchParams to append
 */
export function buildSimduneUrl(endpoint: string, queryParams?: URLSearchParams): string {
  const baseUrl = `${SIMDUNE_API_HOST}/?url=https://api.sim.dune.com${endpoint}`;
  let url = baseUrl;
  if (queryParams && queryParams.toString()) {
    url += `&${queryParams.toString()}`;
  }
  if (process.env.NEXT_PUBLIC_DEVMODE_SHARED_SECRET) {
    url += `${queryParams?.toString() ? "&" : "?"}localkey=${process.env.NEXT_PUBLIC_DEVMODE_SHARED_SECRET}`;
  }
  return url;
}
export async function fetchSimTokenInfo(tokenAddress: string, chainId: number): Promise<SimTokenInfoResponse> {
  if (!tokenAddress) throw new Error("Token address is required");
  if (!chainId) throw new Error("Chain ID is required");

  // SimDune has no SVM token-info endpoint — return empty for Solana chains
  if (SOLANA_CHAIN_IDS.has(chainId)) {
    return {
      contract_address: tokenAddress,
      tokens: []
    };
  }

  // Manual chains (e.g. Plume) are served by our Next.js route instead of Sim Dune.
  if (hasManualTokens(chainId)) {
    const res = await fetch("/api/onchain-token-info", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        address: tokenAddress,
        chainId
      })
    });
    if (!res.ok) {
      return {
        contract_address: tokenAddress,
        tokens: []
      };
    }
    return (await res.json()) as SimTokenInfoResponse;
  }
  const queryParams = new URLSearchParams();
  queryParams.append("chain_ids", chainId.toString());
  const url = buildSimduneUrl(`/v1/evm/token-info/${tokenAddress}`, queryParams);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch token info: ${response.statusText}`);
  }
  const tokenData: SimTokenInfoResponse = await response.json();
  return tokenData;
}

/**
 * Hook to fetch token information using SimDune API
 * @param tokenAddress - The token contract address or 'native' for native token
 * @param chainId - Chain ID to query
 * @param enabled - Whether the query should be enabled
 */
export function useSimTokenInfo(tokenAddress, chainId, t0) {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== chainId || $[1] !== tokenAddress) {
    t1 = ["simTokenInfo", tokenAddress, chainId];
    t2 = () => {
      if (!tokenAddress || !chainId) {
        throw new Error("Missing required parameters");
      }
      return fetchSimTokenInfo(tokenAddress, chainId);
    };
    $[0] = chainId;
    $[1] = tokenAddress;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = enabled && Boolean(tokenAddress) && Boolean(chainId);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 300000
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}

/** Canonical key for `tokenInfoByKey` — `${chainId}:${lowercased address}`. */
export function tokenInfoKey(tokenAddress: string, chainId: number): string {
  return `${chainId}:${normalizeAddress(tokenAddress)}`;
}
export interface UseSimTokensInfoResult {
  /** Plain object for TanStack `replaceEqualDeep` structural sharing — see `apps/b3os-web/CLAUDE.md`. */
  tokenInfoByKey: Record<string, SimTokenInfo>;
  isLoading: boolean;
  isFetching: boolean;
}

/** Batch-fetch token info. Read via `tokenInfoByKey[tokenInfoKey(address, chainId)]`. */
export function useSimTokensInfo(tokens, t0) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== enabled || $[1] !== tokens) {
    let t2;
    if ($[3] !== enabled || $[4] !== tokens.length) {
      t2 = t => ({
        queryKey: ["simTokenInfo", t.tokenAddress, t.chainId],
        queryFn: () => fetchSimTokenInfo(t.tokenAddress, t.chainId),
        staleTime: 300000,
        enabled: enabled && tokens.length > 0
      });
      $[3] = enabled;
      $[4] = tokens.length;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = tokens.map(t2);
    $[0] = enabled;
    $[1] = tokens;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[6] !== tokens) {
    t2 = results => {
      const tokenInfoByKey = {};
      for (let i = 0; i < tokens.length; i++) {
        const token = results[i]?.data?.tokens?.[0];
        if (token) {
          tokenInfoByKey[tokenInfoKey(tokens[i].tokenAddress, tokens[i].chainId)] = token;
        }
      }
      return {
        tokenInfoByKey,
        isLoading: results.some(_temp),
        isFetching: results.some(_temp2)
      };
    };
    $[6] = tokens;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = {
      queries: t1,
      combine: t2
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQueries(t3);
}
function _temp2(r_0) {
  return r_0.isFetching;
}
function _temp(r) {
  return r.isLoading;
}
export async function fetchSimBalance(address: string, chainIdsParam?: number[]): Promise<SimBalanceResponse> {
  if (!address) throw new Error("Address is required");

  // Solana addresses never match a manual EVM chain. Skip the fan-out to
  // avoid per-render 400-response waste on the /api/onchain-balances route.
  const isSolana = isSolanaAddress(address);

  // Partition chains into manual vs Sim Dune. When chainIdsParam is undefined
  // the caller wants "all chains" — include every manual chain explicitly
  // since Sim Dune's "all" naturally excludes them.
  const manualChainIds = isSolana ? [] : chainIdsParam ? chainIdsParam.filter(id => hasManualTokens(id)) : [...MANUAL_CHAIN_IDS];
  const simChainIds = chainIdsParam ? chainIdsParam.filter(id => !hasManualTokens(id)) : undefined;
  let simPromise: Promise<SimBalanceResponse>;
  if (simChainIds && simChainIds.length === 0) {
    const now = new Date().toISOString();
    simPromise = Promise.resolve<SimBalanceResponse>({
      request_time: now,
      response_time: now,
      wallet_address: address,
      balances: []
    });
  } else {
    simPromise = fetchSimBalanceRaw(address, simChainIds);
  }
  const manualPromises = manualChainIds.map(chainId => fetch("/api/onchain-balances", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      address,
      chainId
    })
  }).then(r => r.ok ? r.json() as Promise<SimBalanceResponse> : null).catch(() => null));
  const [simResponse, ...manualResponses] = await Promise.all([simPromise, ...manualPromises]);
  const mergedBalances = [...simResponse.balances, ...manualResponses.flatMap(r => r ? r.balances : [])];
  return {
    ...simResponse,
    balances: mergedBalances
  };
}
async function fetchSimBalanceRaw(address: string, chainIdsParam?: number[]): Promise<SimBalanceResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append("metadata", "logo");
  queryParams.append("exclude_spam_tokens", "true");
  if (chainIdsParam && chainIdsParam.length > 0) {
    queryParams.append("chain_ids", chainIdsParam.join(","));
  }
  const endpoint = isSolanaAddress(address) ? `/beta/svm/balances/${address}` : `/v1/evm/balances/${address}`;
  const url = buildSimduneUrl(endpoint, queryParams);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch balance: ${response.statusText}`);
  }
  const balanceData: SimBalanceResponse = await response.json();
  if (isSolanaAddress(address) && balanceData.balances) {
    for (const b of balanceData.balances) {
      if (!b.chain_id && b.chain === "solana") {
        b.chain_id = SOLANA_MAINNET_CHAIN_ID;
      }
      if (isNativeTokenAddress(b.address)) {
        b.address = "So11111111111111111111111111111111111111112";
      }
    }
  }
  return balanceData;
}

/**
 * Hook to fetch wallet balances using SimDune API
 * @param address - Wallet address to fetch balances for
 * @param chainIds - Optional array of chain IDs to filter by
 * @param enabled - Whether the query should be enabled
 */
export function useSimBalance(address, chainIds, t0) {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== address || $[1] !== chainIds) {
    t1 = ["simBalance", address, chainIds];
    t2 = () => {
      if (!address) {
        throw new Error("Address is required");
      }
      return fetchSimBalance(address, chainIds);
    };
    $[0] = address;
    $[1] = chainIds;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = enabled && Boolean(address);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 60000
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}

// Activity types
export interface SimActivityItem {
  chain_id: number;
  block_number: number;
  block_time: string;
  tx_hash: string;
  type: "send" | "receive" | "mint" | "burn" | "swap" | "approve" | "call";
  asset_type?: "native" | "erc20" | "erc721" | "erc1155";
  /** Top-level tx signer. Distinct from log-level `from`, which is attacker-controlled in log-spoof spam. */
  tx_from?: string;
  /** Top-level tx recipient (contract called). For a real ERC20 transfer this is the token contract or a known router. */
  tx_to?: string;
  from?: string;
  to?: string;
  token_address?: string;
  value?: string;
  value_usd?: number;
  token_metadata?: {
    symbol?: string;
    decimals?: number;
    price_usd?: number;
    name?: string;
    logo?: string;
  };
  gas_used?: number;
  transaction_fee?: number;
}
export interface SimActivityResponse {
  activity: SimActivityItem[];
  next_offset?: string;
}
async function fetchSimActivity(address: string, options?: {
  chainIds?: number[];
  limit?: number;
}): Promise<SimActivityResponse> {
  if (!address) throw new Error("Address is required");
  const queryParams = new URLSearchParams();
  if (options?.chainIds && options.chainIds.length > 0) {
    queryParams.append("chain_ids", options.chainIds.join(","));
  }
  if (options?.limit) {
    queryParams.append("limit", options.limit.toString());
  }
  const url = buildSimduneUrl(`/v1/evm/activity/${address}`, queryParams);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch activity: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Hook to fetch wallet activity using SimDune API
 * @param address - Wallet address to fetch activity for
 * @param options - Optional chain IDs and limit
 * @param enabled - Whether the query should be enabled
 */
export function useSimActivity(address, options, t0) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  const t1 = options?.chainIds;
  const t2 = options?.limit;
  let t3;
  if ($[0] !== address || $[1] !== t1 || $[2] !== t2) {
    t3 = ["simActivity", address, t1, t2];
    $[0] = address;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== address || $[5] !== options) {
    t4 = () => {
      if (!address) {
        throw new Error("Address is required");
      }
      return fetchSimActivity(address, options);
    };
    $[4] = address;
    $[5] = options;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = enabled && Boolean(address);
  let t6;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: 30000
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return useQuery(t6);
}